/**
 * Careers Hero – GSAP ScrambleText title + SplitText chars tagline
 * Matches the animation pattern used in work-portfolio.js (insights/work-portfolio heroes).
 * Triggered via IntersectionObserver (hero is above the fold on page load).
 */
(function () {
  'use strict';

  // Mobile: skip the GSAP scramble/SplitText animation entirely. The title renders
  // statically (same as when GSAP is absent), and with no mobile GSAP consumer the
  // loader skips loading GSAP on this template (see layout/theme.liquid).
  if (window.matchMedia('(max-width: 989px)').matches) return;

  var INIT_FLAG = 'careersHeroInit';

  function animateHero(section) {
    if (typeof gsap === 'undefined') return;

    var titleEl = section.querySelector('.careers-hero__title');
    if (!titleEl) return;

    // Pick the currently visible tagline instance (dual-instance pattern)
    var tagline = null;
    var taglineCandidates = section.querySelectorAll('.careers-hero__tagline');
    for (var i = 0; i < taglineCandidates.length; i++) {
      if (getComputedStyle(taglineCandidates[i]).display !== 'none') {
        tagline = taglineCandidates[i];
        break;
      }
    }

    // Lock title height BEFORE hiding — prevents scramble reflow between line counts
    var naturalHeight = titleEl.offsetHeight;
    var naturalTitleWidth = titleEl.offsetWidth;
    gsap.set(titleEl, { opacity: 0, minHeight: naturalHeight, width: '100vw' });
    if (tagline) gsap.set(tagline, { opacity: 0, willChange: 'opacity' });

    var originalText = titleEl.dataset.chOriginalText || titleEl.textContent || '';
    titleEl.dataset.chOriginalText = originalText;

    var hasScramble = typeof ScrambleTextPlugin !== 'undefined';
    var hasSplitText = typeof SplitText !== 'undefined';
    var taglineSplit = null;

    if (tagline && hasSplitText) {
      try {
        gsap.registerPlugin(SplitText);
        taglineSplit = new SplitText(tagline, { type: 'chars', specialChars: '.,!?:;+-=&/%$#@<>' });
        if (taglineSplit.chars && taglineSplit.chars.length) {
          gsap.set(taglineSplit.chars, { opacity: 0, yPercent: 100, willChange: 'transform, opacity' });
        }
      } catch (e) {
        taglineSplit = null;
      }
    }

    var tl = gsap.timeline({ defaults: { ease: 'power2.out' } });

    // Step 1 — Title: scramble or fade+slide
    if (hasScramble && originalText) {
      tl.to(titleEl, {
        duration: 1.2,
        opacity: 1,
        scrambleText: {
          text: originalText,
          chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
          revealDelay: 0.2,
          speed: 0.5,
        },
        ease: 'none',
        onComplete: function () { gsap.set(titleEl, { minHeight: '' }); },
      });
    } else {
      tl.fromTo(titleEl, { opacity: 0, y: 24 }, {
        opacity: 1, y: 0, duration: 0.8, ease: 'expo.out',
        onComplete: function () { gsap.set(titleEl, { minHeight: '' }); },
      });
    }

    // Step 2 — Title contracts to natural width + tagline reveals simultaneously
    tl.addLabel('reveal', '+=0.3');

    tl.to(titleEl, { width: naturalTitleWidth, duration: 0.6, ease: 'power2.inOut' }, 'reveal');

    if (taglineSplit && taglineSplit.chars && taglineSplit.chars.length) {
      tl.to(tagline, { opacity: 1, duration: 0.3 }, 'reveal');
      tl.to(taglineSplit.chars, { opacity: 1, yPercent: 0, duration: 0.3, stagger: 0.01, ease: 'expo.out' }, 'reveal');
    } else if (tagline) {
      tl.to(tagline, { opacity: 1, duration: 0.3 }, 'reveal');
    }
  }

  function initSection(section) {
    if (!section || section.dataset[INIT_FLAG] === '1') return;
    section.dataset[INIT_FLAG] = '1';

    var heroObs = null;

    if (typeof IntersectionObserver !== 'undefined') {
      heroObs = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;
            heroObs.unobserve(entries[i].target);
            animateHero(section);
            break;
          }
        },
        { threshold: 0.35 }
      );
      heroObs.observe(section);
    } else {
      animateHero(section);
    }

    section._chCleanup = function () {
      if (heroObs) heroObs.disconnect();
      var titleEl = section.querySelector('.careers-hero__title');
      if (titleEl && titleEl.dataset.chOriginalText) {
        titleEl.textContent = titleEl.dataset.chOriginalText;
        delete titleEl.dataset.chOriginalText;
        gsap.set(titleEl, { minHeight: '' });
      }
      delete section.dataset[INIT_FLAG];
      delete section._chCleanup;
    };
  }

  function initAll(root) {
    var sections = (root && root.querySelectorAll) ? root.querySelectorAll('[data-careers-hero]') : [];
    for (var i = 0; i < sections.length; i++) initSection(sections[i]);
  }

  function cleanupSection(container) {
    if (!container) return;
    var sections = (container.querySelectorAll) ? container.querySelectorAll('[data-careers-hero]') : [];
    for (var i = 0; i < sections.length; i++) {
      if (typeof sections[i]._chCleanup === 'function') sections[i]._chCleanup();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      document.fonts.ready.then(function () { initAll(document); });
    });
  } else {
    document.fonts.ready.then(function () { initAll(document); });
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { cleanupSection(e.target); });
})();
